import type { ReactNode } from 'react';

export type StatTone = 'neutral' | 'primary' | 'success' | 'warning' | 'danger';

const toneClasses: Record<StatTone, string> = {
  neutral: 'border-border bg-surface text-text',
  primary: 'border-primary/20 bg-primary/5 text-primary',
  success: 'border-success/30 bg-success/5 text-success',
  warning: 'border-warning/30 bg-warning/5 text-warning',
  danger: 'border-danger/30 bg-danger/5 text-danger',
};

interface StatCardProps {
  /** Kurzer Label (z.B. "Beobachtete Systeme"). */
  label: string;
  /** Hauptzahl (oder String, wenn z.B. "—" für Empty). */
  value: ReactNode;
  /** Lucide-Icon für die rechte Spalte. */
  icon?: ReactNode;
  /** Optionaler Helptext unter der Zahl. */
  helpText?: ReactNode;
  /** Visuelle Tonung. */
  tone?: StatTone;
  /** Test-ID für e2e/unit. */
  testId?: string;
}

/**
 * Mini-Card für Header-Stats (Drift-Dashboard, Reports).
 *
 * Aufbau:
 *   <Label>     <Icon>
 *   <ValueGroß>
 *   <HelpText>
 *
 * Reihen-Variante: Cards in einem `grid-cols-2 md:grid-cols-4`-Container
 * platzieren. Halten sich klein, damit 4 nebeneinander auf Desktop passen.
 */
export function StatCard({
  label,
  value,
  icon,
  helpText,
  tone = 'neutral',
  testId,
}: StatCardProps): JSX.Element {
  return (
    <article
      data-testid={testId}
      className={[
        'flex min-w-0 flex-col gap-1 rounded-lg border p-3 md:p-4',
        toneClasses[tone],
      ].join(' ')}
    >
      <div className="flex items-start justify-between gap-2">
        <p className="truncate text-xs font-medium uppercase tracking-wider opacity-80">
          {label}
        </p>
        {icon && (
          <span className="shrink-0 opacity-70" aria-hidden="true">
            {icon}
          </span>
        )}
      </div>
      <p className="truncate text-2xl font-semibold tabular-nums text-text">{value}</p>
      {helpText && <p className="truncate text-xs text-muted">{helpText}</p>}
    </article>
  );
}
